import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { NAV_GROUPS, QUICK_ACTIONS, hasPerm } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/common/Logo";
import { initials } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { PushToggle } from "@/components/common/PushToggle";
import { NotificationBell } from "@/components/common/NotificationBell";
import { Sun, Moon, Menu, LogOut, ChevronDown, ChevronRight, Plus, PanelLeftClose, PanelLeftOpen } from "lucide-react";

const STORAGE_KEY = "hi_nav_open";
const COLLAPSE_KEY = "hi_sidebar_collapsed";

// gruppi visibili in base ai permessi
const visibleGroups = (user) =>
  NAV_GROUPS.map((g) => {
    if (g.children) {
      const children = g.children.filter((c) => hasPerm(user, c.perm));
      return children.length ? { ...g, children } : null;
    }
    return hasPerm(user, g.perm) ? g : null;
  }).filter(Boolean);

const childLinkClass = ({ isActive }) =>
  `block rounded-md pl-11 pr-3 py-2 text-sm transition-colors ${
    isActive
      ? "bg-[hsl(var(--hi-gold-soft))] text-foreground font-medium border border-[hsl(var(--hi-gold)/0.35)]"
      : "text-muted-foreground hover:bg-accent hover:text-foreground border border-transparent"
  }`;

const singleLinkClass = ({ isActive }) =>
  `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
    isActive
      ? "bg-[hsl(var(--hi-gold-soft))] text-foreground border border-[hsl(var(--hi-gold)/0.35)]"
      : "text-muted-foreground hover:bg-accent hover:text-foreground border border-transparent"
  }`;

function GroupedNav({ user, onNav }) {
  const location = useLocation();
  const groups = visibleGroups(user);

  const activeGroupKey = groups.find((g) =>
    g.children ? g.children.some((c) => location.pathname === c.to || (c.to !== "/" && location.pathname.startsWith(c.to)))
               : (g.to === "/" ? location.pathname === "/" : location.pathname.startsWith(g.to))
  )?.key;

  const [open, setOpen] = useState(() => {
    let saved = {};
    try { saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch (e) { saved = {}; }
    return saved;
  });

  // apre automaticamente il gruppo della pagina attiva (menu memory)
  useEffect(() => {
    if (activeGroupKey) setOpen((o) => (o[activeGroupKey] ? o : { ...o, [activeGroupKey]: true }));
  }, [activeGroupKey]);

  const toggle = (key) => setOpen((o) => {
    const next = { ...o, [key]: !o[key] };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (e) { console.debug("nav state persist skipped", e); }
    return next;
  });

  return (
    <nav className="flex flex-col gap-1 px-3">
      {groups.map((g) => {
        const Icon = g.icon;
        if (!g.children) {
          return (
            <NavLink key={g.key} to={g.to} end={g.to === "/"} onClick={onNav}
              data-testid={`nav-${g.key}`} className={singleLinkClass}>
              {({ isActive }) => (<><Icon className={`h-[18px] w-[18px] ${isActive ? "text-[hsl(var(--hi-gold))]" : ""}`} /><span>{g.label}</span></>)}
            </NavLink>
          );
        }
        const isOpen = !!open[g.key];
        const isActiveGroup = activeGroupKey === g.key;
        return (
          <div key={g.key}>
            <button type="button" onClick={() => toggle(g.key)} data-testid={`nav-group-${g.key}`}
              className={`w-full group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActiveGroup ? "text-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
              <Icon className={`h-[18px] w-[18px] ${isActiveGroup ? "text-[hsl(var(--hi-gold))]" : ""}`} />
              <span className="flex-1 text-left">{g.label}</span>
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            {isOpen && (
              <div className="mt-1 space-y-1">
                {g.children.map((c) => (
                  <NavLink key={c.to} to={c.to} onClick={onNav}
                    data-testid={`nav-${c.to.replace(/\//g, "") || "home"}`} className={childLinkClass}>
                    {c.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

function QuickActions({ user }) {
  const navigate = useNavigate();
  const actions = QUICK_ACTIONS.filter((a) => hasPerm(user, a.perm));
  if (!actions.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="gap-1.5" data-testid="quick-actions-button"><Plus className="h-4 w-4" /> <span className="hidden sm:inline">Nuovo</span></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Azioni rapide</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {actions.map((a) => (
          <DropdownMenuItem key={a.label} onClick={() => navigate(a.to)} data-testid={`quick-${a.to.replace(/\//g, "")}`}>
            {a.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export const AppShell = ({ children }) => {
  const { user, logout, theme, setTheme } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Menu laterale a scomparsa (desktop): preferenza memorizzata sul dispositivo.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch (e) { return false; }
  });
  const navigate = useNavigate();

  const toggleCollapsed = () => setCollapsed((c) => {
    const next = !c;
    try { localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0"); } catch (e) { console.debug("sidebar state skipped", e); }
    return next;
  });

  return (
    <div className="min-h-screen hi-app-bg">
      {/* Sidebar desktop (nascondibile) */}
      <aside className={`hidden ${collapsed ? "" : "lg:flex"} fixed inset-y-0 left-0 w-[280px] flex-col bg-card border-r border-border z-30`}>
        <div className="h-16 flex items-center px-5 border-b border-border">
          <button onClick={() => navigate("/")} className="cursor-pointer"><Logo showText size={40} /></button>
        </div>
        <div className="flex-1 overflow-y-auto py-4 hi-scroll">
          <GroupedNav user={user} />
        </div>
        <div className="p-3 border-t border-border text-[11px] text-muted-foreground">
          Identity OS · v1
        </div>
      </aside>

      {/* Main */}
      <div className={`transition-[padding] duration-200 ${collapsed ? "" : "lg:pl-[280px]"}`}>
        {/* Topbar */}
        <header className="sticky top-0 z-20 h-16 flex items-center justify-between gap-3 px-4 md:px-8 bg-background/80 backdrop-blur border-b border-border">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[280px] bg-card overflow-y-auto hi-scroll">
                <div className="h-16 flex items-center px-5 border-b border-border"><Logo showText /></div>
                <div className="py-4"><GroupedNav user={user} onNav={() => setMobileOpen(false)} /></div>
              </SheetContent>
            </Sheet>
            {/* Toggle desktop: nasconde/mostra il menu laterale */}
            <Button variant="ghost" size="icon" onClick={toggleCollapsed}
              className="hidden lg:inline-flex" data-testid="sidebar-toggle"
              aria-label={collapsed ? "Mostra menu" : "Nascondi menu"} title={collapsed ? "Mostra menu" : "Nascondi menu"}>
              {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
            </Button>
            <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
              <span className="h-2 w-2 rounded-full bg-[hsl(var(--hi-status-success))]" />
              Hair Identity — Rende
            </div>
          </div>

          <div className="flex items-center gap-2">
            <QuickActions user={user} />
            {["admin", "manager"].includes(user?.role) && (
              <span title="Notifiche" aria-label="Notifiche"><NotificationBell /></span>
            )}
            <Button
              variant="ghost" size="icon" data-testid="theme-toggle"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              aria-label="Cambia tema"
            >
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu-button">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-[hsl(var(--hi-gold-soft))] text-[hsl(var(--hi-gold))] text-xs font-semibold">
                      {initials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden md:block text-left leading-tight">
                    <div className="text-sm font-medium">{user?.name}</div>
                    <div className="text-[11px] text-muted-foreground capitalize">{user?.job_title || user?.role}</div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5 flex items-center justify-between gap-2" onClick={(e) => e.stopPropagation()} data-testid="user-menu-push-row">
                  <span className="text-sm text-muted-foreground">Notifiche dispositivo</span>
                  <PushToggle />
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-destructive">
                  <LogOut className="h-4 w-4 mr-2" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="px-4 md:px-8 py-6 md:py-8 max-w-[1600px] mx-auto">{children}</main>
      </div>
    </div>
  );
};
